import { Sparkles } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { toneSoftClasses } from "@/lib/tones";

export type TagVariant = "area" | "skill" | "manual" | "auto";

const VARIANT_CLASSES: Record<TagVariant, string> = {
  area: toneSoftClasses.neutral,
  skill: "bg-background text-foreground border-border",
  manual: "bg-background text-foreground border-border",
  auto: toneSoftClasses.ai,
};

interface TagListProps {
  items: (string | null | undefined)[] | null | undefined;
  /** Quantas mostrar antes do "+N". */
  max?: number;
  variant?: TagVariant;
  /** Tags a destacar primeiro (ex.: a área filtrada). */
  highlight?: string[];
  size?: "xs" | "sm";
  className?: string;
}

export function TagList({ items, max = 3, variant = "area", highlight = [], size = "sm", className }: TagListProps) {
  const clean = Array.from(new Set((items ?? []).filter((item): item is string => !!item && item.trim() !== "")));
  if (clean.length === 0) return null;
  const highlighted = new Set(highlight.map((item) => item.toLowerCase()));
  const ordered = [
    ...clean.filter((item) => highlighted.has(item.toLowerCase())),
    ...clean.filter((item) => !highlighted.has(item.toLowerCase())),
  ];
  const visible = ordered.slice(0, max);
  const hidden = ordered.slice(max);
  const chip = cn(
    "inline-flex items-center gap-1 whitespace-nowrap rounded-full border font-medium",
    size === "xs" ? "h-5 px-1.5 text-[11px]" : "h-6 px-2 text-xs",
  );

  return (
    <span className={cn("inline-flex flex-wrap items-center gap-1", className)}>
      {visible.map((item) => (
        <span
          key={item}
          className={cn(chip, VARIANT_CLASSES[variant], highlighted.has(item.toLowerCase()) && "ring-1 ring-primary/40")}
        >
          {variant === "auto" && <Sparkles className="h-3 w-3" />}
          {item}
        </span>
      ))}
      {hidden.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn(chip, "cursor-default border-dashed bg-background text-muted-foreground")}>+{hidden.length}</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{hidden.join(", ")}</TooltipContent>
        </Tooltip>
      )}
    </span>
  );
}
